/**
 * Отчёты: по диспетчерам и по источникам.
 *
 * CR и CPL считаются в SQL и приходят готовыми: второй реализации на клиенте
 * не будет, иначе цифры разойдутся между вкладками (§10.2).
 * Режим кассы — деньги по дате прихода номеров; когортный — расход партии
 * против лидов, пришедших с этой партии за окно атрибуции (§10.1).
 * Отчёт подписывает применённые настройки: порог дозвона менялся — цифры
 * за разные периоды посчитаны разными правилами (§7.5).
 */
import { z } from 'zod';
import { bigIdSchema, businessDateSchema, isoDateTimeSchema, refIdSchema } from './common.dto.js';
import { moneySchema } from './sources.dto.js';
/** Как считать деньги источника: касса или когорта */
export const statsModeSchema = z.enum(['cash', 'cohort']);
/** Подпись отчёта: с какими настройками он посчитан */
export const statsAppliedSettingsSchema = z.object({
    reachedMinSec: z.number().int().min(0),
    reachedMinSecChangedAt: isoDateTimeSchema.nullable(),
    attributionWindowDays: z.number().int().min(1),
});
export const dispatcherStatsQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
    dispatcherId: bigIdSchema.optional(),
    /** Разбивка по дням вместо одной строки за период */
    byDay: z.enum(['true', 'false']).default('false'),
});
export const dispatcherStatsRowSchema = z.object({
    dispatcherId: bigIdSchema,
    fullName: z.string(),
    businessDate: businessDateSchema.nullable(),
    assignedCnt: z.number().int().min(0),
    labeledCnt: z.number().int().min(0),
    callsCnt: z.number().int().min(0),
    uniqueCalledCnt: z.number().int().min(0).default(0),
    uniqueContactCnt: z.number().int().min(0).default(0),
    /** Сняли трубку технически — включая автоответчики: показатель связи */
    reachedCnt: z.number().int().min(0),
    /** Живые разговоры по меткам: дозвон, каким его считают люди (31.08.2026) */
    contactCnt: z.number().int().min(0),
    leadsCnt: z.number().int().min(0),
    negativeCnt: z.number().int().min(0),
    talkSec: z.number().int().min(0),
    /** Лиды ÷ выданные контакты */
    cr: z.number().min(0),
    /** Дозвон: живые разговоры (по меткам) ÷ наборы */
    reachRate: z.number().min(0),
    /** Сколько оператор снял за звонки этого диспетчера, рубли */
    telephonySpend: moneySchema,
    /** Заработок: лиды × ставка pay.per_lead_rub; null — ставка не задана */
    earned: moneySchema.nullable(),
    purchaseSpend: moneySchema.optional(),
    payPerLeadRub: z.number().nonnegative().optional(),
    totalSpend: moneySchema.optional(),
    cplTotal: moneySchema.nullable().optional(),
});
export const dispatcherStatsResponseSchema = z.object({
    unique: z.object({ calledCnt: z.number().int().nonnegative(), contactCnt: z.number().int().nonnegative() }),
    rows: z.array(dispatcherStatsRowSchema),
    applied: statsAppliedSettingsSchema,
});
export const sourceStatsQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
    mode: statsModeSchema.default('cash'),
    cabinetId: z.coerce.number().int().positive().optional(),
    sourceId: z.coerce.number().int().positive().optional(),
});
export const sourceStatsRowSchema = z.object({
    sourceId: refIdSchema,
    sourceTitle: z.string(),
    cabinetTitle: z.string(),
    contactsReceived: z.number().int().min(0),
    /** Сколько из принятых отбито дедупом: это оплаченные повторы (§6.8) */
    duplicatesCnt: z.number().int().min(0),
    contactsCalled: z.number().int().min(0),
    reachedCnt: z.number().int().min(0),
    leadsCnt: z.number().int().min(0),
    talkSec: z.number().int().min(0),
    spend: moneySchema,
    /** Лиды ÷ контакты */
    cr: z.number().min(0),
    /** Расход ÷ лиды. Без лидов — null, а не бесконечность */
    cpl: moneySchema.nullable(),
});
export const sourceStatsResponseSchema = z.object({
    rows: z.array(sourceStatsRowSchema),
    mode: statsModeSchema,
    applied: statsAppliedSettingsSchema,
});
/** GET /api/stats/summary — верхняя плашка админки за сегодня */
export const statsSummaryResponseSchema = z.object({
    businessDate: businessDateSchema,
    contactsReceived: z.number().int().min(0),
    assignedCnt: z.number().int().min(0),
    callsCnt: z.number().int().min(0),
    uniqueCalledCnt: z.number().int().min(0).default(0),
    uniqueContactCnt: z.number().int().min(0).default(0),
    reachedCnt: z.number().int().min(0),
    /** Живые разговоры по меткам — «Дозвон» плашки считается по ним */
    contactCnt: z.number().int().min(0),
    /** Чем оказались ответы по звуку: живой / помощник / тишина / информатор */
    answers: z.object({
        answered: z.number().int().min(0),
        reviewed: z.number().int().min(0),
        live: z.number().int().min(0),
        assistant: z.number().int().min(0),
        silence: z.number().int().min(0),
        informer: z.number().int().min(0),
    }),
    leadsCnt: z.number().int().min(0),
    /** Закупка контактов, рубли */
    spend: moneySchema,
    /** Связь: наборы по 0,74 ₽ и минуты по 6 ₽ (тариф в настройках телефонии) */
    telephonySpend: moneySchema,
    /** Премия диспетчерам: лиды × ставка pay.per_lead_rub */
    paySpend: moneySchema,
    dispatcherLeadsCnt: z.number().int().nonnegative().optional(),
    robotLeadsCnt: z.number().int().nonnegative().optional(),
    robotSpend: moneySchema.optional(),
    robotUnmatched: z.number().int().nonnegative().optional(),
    /** Закупка, связь и премия — настоящие деньги за сутки */
    spendTotal: moneySchema,
    /** Цена лида по одной закупке: так считалось до появления тарифа */
    cpl: moneySchema.nullable(),
    /** Цена лида с учётом связи и премии диспетчерам */
    cplTotal: moneySchema.nullable(),
    dispatchersOnShift: z.number().int().min(0),
});
/**
 * GET /api/stats/leads — журнал лидов с комментарием диспетчера.
 *
 * Отдельный экран, а не колонка в отчёте: отчёт отвечает «сколько», а здесь
 * читают «о чём договорились». Комментарий — единственное, ради чего лид
 * открывают, поэтому он приходит целиком, без обрезки.
 */
export const leadsQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
    dispatcherId: bigIdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const leadRowSchema = z.object({
    assignmentId: bigIdSchema,
    contactId: bigIdSchema,
    /** Полный номер: экран за логином, маскируют только канал в телеграме */
    phoneE164: z.string(),
    fullName: z.string().nullable(),
    labeledAt: isoDateTimeSchema.nullable(),
    businessDate: businessDateSchema,
    roundNumber: z.number().int().min(1),
    labelCode: z.string().nullable(),
    labelTitle: z.string().nullable(),
    comment: z.string().nullable(),
    dispatcherId: bigIdSchema.nullable(),
    /** null — лид от робота: диспетчера у него нет */
    dispatcherName: z.string().nullable(),
    sourceId: refIdSchema.nullable(),
    sourceTitle: z.string().nullable(),
    callsCnt: z.number().int().min(0),
    talkSec: z.number().int().min(0),
    isReached: z.boolean(),
});
export const leadsResponseSchema = z.object({
    rows: z.array(leadRowSchema),
    total: z.number().int().min(0),
});
/**
 * GET /api/stats/reach — дозвон человеческим языком.
 *
 * Результаты сгруппированы по смыслу, а не по коду станции: «поговорили»,
 * «взяли трубку, но разговора не вышло», «не взяли», «не дошло по нашей вине».
 * Последняя группа отдельно намеренно — сеть и блокировки антиспамом лечатся
 * не тем же, чем плохая база.
 */
/** GET /api/stats/reach — период отчёта по дозвону */
export const reachQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
});
export const reachRowSchema = z.object({
    disposition: z.string(),
    title: z.string(),
    group: z.enum(['talked', 'picked_up_no_talk', 'no_pickup', 'our_problem']),
    calls: z.number().int().min(0),
    sharePct: z.number().min(0),
    medianTalkSec: z.number().int().min(0).nullable(),
});
/** Сколько разговоров какой длины: по горбу видно автоответчики */
export const talkBucketSchema = z.object({
    key: z.string(),
    title: z.string(),
    calls: z.number().int().min(0),
    /** Доля от состоявшихся разговоров, а не от всех наборов */
    sharePct: z.number().min(0),
});
/**
 * GET /api/stats/labels — чем закрыты назначения за период.
 *
 * Разрез диспетчерской оценки рядом с отчётом по дозвону: диспозиция говорит,
 * что услышала станция, метка — что услышал человек. «Автоответчик» от
 * «не взял трубку» отличает только диспетчер.
 */
export const labelBreakdownQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
});
export const labelBreakdownRowSchema = z.object({
    labelId: refIdSchema,
    code: z.string(),
    title: z.string(),
    /** Цвет из справочника меток; null у заведённых до появления цвета */
    color: z.string().nullable(),
    isLead: z.boolean(),
    isNegative: z.boolean(),
    labeledCnt: z.number().int().min(0),
    /** Доля от всех размеченных за период, проценты с одним знаком */
    sharePct: z.number().min(0),
});
export const labelBreakdownResponseSchema = z.object({
    /** Всего назначений закрыто меткой за период — знаменатель долей */
    total: z.number().int().min(0),
    rows: z.array(labelBreakdownRowSchema),
});
export const reachReportResponseSchema = z.object({
    attempts: z.number().int().min(0),
    pickedUp: z.number().int().min(0),
    talked: z.number().int().min(0),
    pickedUpNoTalk: z.number().int().min(0),
    noPickup: z.number().int().min(0),
    ourProblem: z.number().int().min(0),
    talkRatePct: z.number().min(0),
    pickupRatePct: z.number().min(0),
    rows: z.array(reachRowSchema),
    talkBuckets: z.array(talkBucketSchema),
    /** С каким порогом разговора посчитано */
    reachedMinSec: z.number().int().min(0),
});
/**
 * GET /api/stats/reach/trunks — сравнение транков за период (18.10.2026).
 *
 * Пять групп звонков (поговорили, взяли без разговора, не взяли, сбросила
 * детекция АО, не дошло по нашей вине) складываются ровно в calls. Лиды и живые
 * разговоры — по меткам, отнесённым к транку последнего звонка карточки.
 * Период — тот же reachQuerySchema.
 */
const trunkReachCountsSchema = z.object({
    calls: z.number().int().min(0),
    pickedUp: z.number().int().min(0),
    talked: z.number().int().min(0),
    pickedUpNoTalk: z.number().int().min(0),
    noPickup: z.number().int().min(0),
    /** «Не отвечает» с кодом 29 — после ответа сбросила детекция автоответчика провайдера */
    cut29: z.number().int().min(0),
    ourProblem: z.number().int().min(0),
    labeled: z.number().int().min(0),
    contacts: z.number().int().min(0),
    leads: z.number().int().min(0),
    pickupRatePct: z.number().min(0),
    talkRatePct: z.number().min(0),
    cut29Pct: z.number().min(0),
    ourProblemPct: z.number().min(0),
    leadPerCallPct: z.number().min(0),
    leadPerContactPct: z.number().min(0),
    /** Расход на связь по тарифу транка, рубли (23.10.2026) */
    telephonySpend: moneySchema,
    /** Связь на один живой разговор; null — разговоров не было */
    spendPerContact: moneySchema.nullable(),
    /** Связь на одну заявку; null — заявок не было */
    spendPerLead: moneySchema.nullable(),
});
export const trunkReachTrunkSchema = trunkReachCountsSchema.extend({
    trunkId: z.number().int(),
    name: z.string(),
    login: z.string().nullable(),
    isActive: z.boolean(),
    registered: z.boolean(),
    /** Тариф, по которому посчитана «Связь», копейки; own — своя цена транка, иначе общий тариф */
    tariff: z.object({
        attemptKop: z.number().int().min(0),
        minuteKop: z.number().int().min(0),
        own: z.boolean(),
        /** Доплата за детекцию АО за набор и с какого дня (26.10.2026); null — в периоде её не было */
        amd: z.object({ kop: z.number().int().min(0), from: z.string() }).nullable(),
    }),
});
export const trunkReachDispatcherSchema = trunkReachCountsSchema.extend({
    trunkId: z.number().int(),
    dispatcherId: z.string(),
    fullName: z.string(),
});
export const trunkReachResponseSchema = z.object({
    trunks: z.array(trunkReachTrunkSchema),
    byDispatcher: z.array(trunkReachDispatcherSchema),
    reachedMinSec: z.number().int().min(0),
    /** Включено ли сейчас чередование транков по звонкам */
    rotationOn: z.boolean(),
    /** Когда последний раз переключали чередование, ISO; null — ни разу */
    rotationChangedAt: z.string().nullable(),
});
/** GET /api/stats/funnel — воронка дозвона за период, одним блоком на Обзоре */
export const funnelQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
});
export const funnelCountsSchema = z.object({
    calls: z.number().int().min(0),
    labeled: z.number().int().min(0),
    contacts: z.number().int().min(0),
    leads: z.number().int().min(0),
    noAnswer: z.number().int().min(0),
    voicemail: z.number().int().min(0),
    silence: z.number().int().min(0),
    other: z.number().int().min(0),
});
export const funnelResponseSchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
    total: funnelCountsSchema,
    /** По уникальным номерам: набрано, живой разговор, живой с первого круга, лиды */
    byPhone: z.object({
        phones: z.number().int().min(0),
        livePhones: z.number().int().min(0),
        liveRound1: z.number().int().min(0),
        leads: z.number().int().min(0),
    }),
    /** По кругам: dispatcherId null — итог; contacts — номеров дошло до круга, firstLive — ответили впервые */
    byRound: z.array(z.object({
        dispatcherId: bigIdSchema.nullable(),
        round: z.number().int().min(1),
        contacts: z.number().int().min(0),
        live: z.number().int().min(0),
        firstLive: z.number().int().min(0),
        leads: z.number().int().min(0),
        totalContacts: z.number().int().min(0),
    })),
    byDispatcher: z.array(funnelCountsSchema.extend({
        dispatcherId: bigIdSchema,
        fullName: z.string(),
        /** Уникальных номеров в работе у диспетчера — знаменатель «дозвона от номера» (09.09.2026) */
        phones: z.number().int().min(0),
        /** Из них тех, где состоялся живой разговор */
        livePhones: z.number().int().min(0),
        /** Из них тех, где поставлен лид */
        leadPhones: z.number().int().min(0),
    })),
    /** По часам МСК: наборы и метки живой / автоответчик / тишина */
    byHour: z.array(z.object({
        hour: z.number().int().min(0).max(23),
        calls: z.number().int().min(0),
        contacts: z.number().int().min(0),
        voicemail: z.number().int().min(0),
        silence: z.number().int().min(0),
    })),
});
/** GET /api/stats/robot — «Робот после девочек»: отдано Sasha после кругов, дозвон и лиды робота */
export const robotReportCountsSchema = z.object({
    handed: z.number().int().min(0),
    sent: z.number().int().min(0),
    skipped: z.number().int().min(0),
    called: z.number().int().min(0),
    reached: z.number().int().min(0),
    leads: z.number().int().min(0),
    noResult: z.number().int().min(0),
});
export const statsRobotResponseSchema = z.object({
    activity: z.object({ results: z.number(), linked: z.number(), unmatched: z.number(), phones: z.number(), leadPhones: z.number(), additionalLeads: z.number(), overlapLeads: z.number(), talkSec: z.number(), estimatedSpend: moneySchema, priceMinuteKop: z.number(), billingStepSec: z.number(), latest: isoDateTimeSchema.nullable() }).optional(),
    from: businessDateSchema,
    to: businessDateSchema,
    total: robotReportCountsSchema,
    byDay: z.array(robotReportCountsSchema.extend({ day: businessDateSchema })),
    bySource: z.array(robotReportCountsSchema.extend({ sourceId: z.number().int().nullable(), sourceTitle: z.string() })),
});
//# sourceMappingURL=stats.dto.js.map